import { isValid } from '@fnando/cpf'
import valid from 'card-validator' // import statement
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { FC, ReactElement, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import { AcceptCreditCards } from '../../components/footer'
import H2 from '../../components/h2'
import Input from '../../components/input'
import { trialDate } from '../../components/loggedNavigation'
import Modal from '../../components/modal'
import { useAuth } from '../../hooks/auth'
import { usePlans } from '../../hooks/plans'
import { toReais } from '../../utils/formatter'
import { ContainerSelectPlan, FooterSelectPlan } from './style'

const schema = yup.object().shape({
  name: yup.string().required('Nome é obrigatório'),
  zipCode: yup.string().min(8, 'CEP inválido').required('CEP é obrigatório'),
  cpf: yup.string().test('test-cpf', 'CPF Inválido', value => isValid(value || '')).required('CPF é obrigatório'),
  creditCard: yup.string().test('test-cc', 'Número inválido', value => valid.number(value).isValid).required('Numero é obrigatório'),
  valid: yup.string().min(5, 'Validade inválida').required('Validade obrigatória'),
  cvv: yup.string().min(3, 'CVV inválido').required('CVV é obrigatório')
})

type recTypes = 'month' | 'year' | 'day' | 'week'

const rec = {
  month: '/mês',
  year: '/ano',
  day: '/dia',
  week: '/semana'
}

const initialValues = {
  name: '',
  zipCode: '',
  cpf: '',
  creditCard: '',
  valid: '',
  cvv: ''
}

const ModalSelectPlan: FC = (): ReactElement | null => {
  const { search } = useLocation()
  const history = useHistory()

  const { user } = useAuth()
  const trial = trialDate(user)

  const { plans, subscribe, get } = usePlans()

  const query = new URLSearchParams(search)

  const planId = query.get('planId')

  const plan = useMemo(() => {
    return plans.find(p => p._id === planId)
  }, [plans, planId])

  const submit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>
  ): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      await subscribe({
        number: values.creditCard.replace(/[^0-9]/gi, ''),
        holderName: values.name,
        expMonth: values.valid.split('/')[0],
        expYear: values.valid.split('/')[1],
        cvv: values.cvv,
        // couponCode
        zipCode: values.zipCode,
        personNumber: values.cpf.replace(/[^0-9]/gi, ''),
        planId: planId || ''
      })
      try {
        await get({ status: 'active' })
      } catch {}
      history.replace({ search: '' })
    } catch { }
    helpers.setSubmitting(false)
  }

  if (query.get('modal') !== 'plan' || !planId || !plan) return null

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={submit}
    >
      {({ isValid, isSubmitting, submitForm }) => (
        <Modal
          isLoading={isSubmitting}
          okButtonText='PAGAR'
          variantButton={isValid ? 'primary' : 'muted'}
          title='Pagar via cartão de crédito'
          onOk={submitForm}
          onClose={() => history.replace({ search: '' })}
          footerComponent={
            <FooterSelectPlan>
              {trial && (
                <div className='info'>
                  *Nenhuma cobrança será feita agora, você tem {trial} dias para
                  testar antes da primeira cobrança e poderá cancelar a qualquer
                  momento.
                </div>
              )}
              <AcceptCreditCards />
            </FooterSelectPlan>
          }
        >
          <ContainerSelectPlan>
            <H2>{toReais(plan?.pricing[0].price)} {rec[plan?.pricing[0].recurrency as recTypes]}</H2>
            <Form>
              <Field
                component={Input}
                name='cpf'
                label='CPF do dono do cartão'
                type='tel'
              />
              <Field
                component={Input}
                name='zipCode'
                label='CEP'
                type='tel'
              />
              <Field
                component={Input}
                name='name'
                label='Nome no cartão'
              />
              <Field
                component={Input}
                name='creditCard'
                mask='9999 9999 9999 9999'
                label='Número do cartão de crédito'
                type='tel'
              />
              <div className='half'>
                <Field
                  component={Input}
                  name='valid'
                  mask='99/99'
                  label='Validade (MM/AA)'
                  type='tel'
                />
                <Field
                  component={Input}
                  name='cvv'
                  mask='9999'
                  label='CVV'
                  type='tel'
                />
              </div>
            </Form>
          </ContainerSelectPlan>
        </Modal>
      )}
    </Formik>
  )
}

export default ModalSelectPlan
